import "./index.scss";
import * as _ from "lodash";
import Moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../../../components/core/table";
import Popup from "../../../../../../components/core/popup";
import DateRangePicker from "../../../../../../components/core/daterangepicker";
import { useTranslation } from "react-i18next";
import {
  addProfileBusiness,
  editProfileBusiness,
  listProfileBusinessHourByProfileId,
  listProfileSpecialDayByProfileId,
  removeProfileBusiness,
} from "../../../../../../store/profile-business/thunkAction";
import {
  BusinessDates,
  ShortBusinessDates,
} from "../../../../../../constant/enums";
import BusinessHour from "../../../../../../components/profile/business-hour";
import {
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../../../../utils/event";

const ProfileBusinessHours = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const params = useParams();
  const profileId = _.get(params, "id");
  const dayKeys = _.keys(BusinessDates);

  useEffect(() => {
    dispatch(listProfileBusinessHourByProfileId(profileId));
    dispatch(listProfileSpecialDayByProfileId(profileId));
  }, [profileId, dispatch]);

  const profileBusinessManage = useSelector(
    (state) => state.profileBusinessManage
  );
  const profileBusinessHours = _.get(
    profileBusinessManage,
    "profileBusinessHours"
  );
  const profileSpecialDays = _.get(profileBusinessManage, "profileSpecialDays");
  const isLoadingAddProfileBusiness = _.get(
    profileBusinessManage,
    "isLoadingAddProfileBusiness",
    false
  );

  const businessHours = _.groupBy(profileBusinessHours, "date");

  const [isOpenSpecialDay, setIsOpenSpecialDay] = useState(false);
  const [businessHour, setBusinessHour] = useState({});
  const [specialDay, setSpecialDay] = useState({});
  const [isOpenBusinessHours, setIsOpenBusinessHour] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [openHours, setOpenHours] = useState([]);
  const [startAt, setStartAt] = useState(Moment().valueOf());
  const [endAt, setEndAt] = useState(Moment().valueOf());
  const [name, setName] = useState("");
  const [otherDays, setOtherDays] = useState([]);

  useEffect(() => {
    setOtherDays([]);
  }, [openHours]);

  const closePopupBusinessHours = () => {
    setIsOpenBusinessHour(false);
  };

  const closePopupSpecialDay = () => {
    setIsOpenSpecialDay(false);
  };

  const onSelectBusinessHours = (businessHour) => {
    const openHour = _.get(businessHour, "openHour", []);
    const isClosed = _.get(businessHour, "isClosed", false);

    setBusinessHour(businessHour);
    setIsClosed(isClosed);
    setOpenHours(openHour);
    setOtherDays([]);
    setIsOpenBusinessHour(true);
  };

  const onSelectSpecialDay = (specialDay) => {
    const name = _.get(specialDay, "name", "");
    const openHour = _.get(specialDay, "openHour", []);
    const isClosed = _.get(specialDay, "isClosed", false);
    const startAt = _.get(specialDay, "startAt", Moment().valueOf());
    const endAt = _.get(specialDay, "endAt", Moment().valueOf());

    setSpecialDay(specialDay);
    setName(name);
    setIsClosed(isClosed);
    setOpenHours(openHour);
    setStartAt(startAt);
    setEndAt(endAt);
    setIsOpenSpecialDay(true);
  };

  const onChangeDateRangePicker = (dateRanger) => {
    const start = _.get(dateRanger, "start");
    const end = _.get(dateRanger, "end");

    setStartAt(start);
    setEndAt(end);
  };

  const onApplyOtherDay = async (date) => {
    const businessDate = _.get(businessHours, date, [])[0];

    const data = {
      profileId,
      date,
      isClosed,
      openHour: openHours,
      isDate: true,
    };

    try {
      if (!businessDate) {
        if (isLoadingAddProfileBusiness) {
          return;
        }
        await dispatch(addProfileBusiness(data)).unwrap();
      } else {
        const editBusinessHourId = _.get(businessDate, "_id");
        await dispatch(
          editProfileBusiness({
            id: editBusinessHourId,
            data: {
              openHour: openHours,
              isClosed,
            },
          })
        ).unwrap();
      }
    } catch (error) {}
  };

  const onSaveBusinessHours = async () => {
    const businessHourId = _.get(businessHour, "_id", "");

    const isError = openHours.some((hour) => hour.isError);

    if (isError) {
      sendNotifyIziMessageError(
        t("settings.profile.business.business_hours_error")
      );
      return;
    }

    const data = {
      profileId,
      date: _.get(businessHour, "date"),
      isClosed,
      openHour: openHours,
      isDate: true,
    };

    try {
      if (businessHourId) {
        await dispatch(
          editProfileBusiness({
            id: businessHourId,
            data,
          })
        ).unwrap();
      } else {
        if (isLoadingAddProfileBusiness) {
          return;
        }
        await dispatch(addProfileBusiness(data)).unwrap();
      }

      sendNotifyIziMessageSuccess(t("profile.profile_business_save_success"));

      for (const date of otherDays) {
        await onApplyOtherDay(date);
      }

      dispatch(listProfileBusinessHourByProfileId(profileId));
      closePopupBusinessHours();
    } catch (error) {
      sendNotifyIziMessageError(t("profile.profile_business_save_error"));
    }
  };

  const onSaveSpecialDay = async () => {
    const specialDayId = _.get(specialDay, "_id", "");
    const now = Moment().startOf("day").valueOf();

    const isError = openHours.some((hour) => hour.isError);

    if (isError) {
      sendNotifyIziMessageError(
        t("settings.profile.business.business_hours_error")
      );
      return;
    }

    if (startAt < now || endAt < startAt) {
      sendNotifyIziMessageError(
        t("settings.profile.business.special_days_error")
      );
      return;
    }

    const data = {
      profileId,
      name,
      startAt,
      endAt,
      isClosed,
      openHour: openHours,
      isDate: false,
    };

    try {
      if (specialDayId) {
        await dispatch(
          editProfileBusiness({
            id: specialDayId,
            data,
          })
        ).unwrap();
      } else {
        if (isLoadingAddProfileBusiness) {
          return;
        }

        await dispatch(addProfileBusiness(data)).unwrap();
      }

      sendNotifyIziMessageSuccess(
        t("profile.profile_special_day_save_success")
      );
      dispatch(listProfileSpecialDayByProfileId(profileId));
      closePopupSpecialDay();
    } catch (error) {
      sendNotifyIziMessageSuccess(t("profile.profile_special_day_save_error"));
    }
  };

  const configTable = {
    header: [
      {
        key: "name",
        label: "NAME",
        className: "small",
        render: (row) => row.name,
      },
      {
        key: "date",
        label: "DATE",
        className: "small",
        render: (row) => {
          const startAt = _.get(row, "startAt");
          const endAt = _.get(row, "endAt");

          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>{`${Moment(startAt).format("DD/MM/YYYY")}-${Moment(
                endAt
              ).format("DD/MM/YYYY")}`}</span>
            </div>
          );
        },
      },
      {
        key: "time",
        label: "TIME",
        className: "small",
        render: (row) => {
          const openHour = _.get(row, "openHour", []);
          const isClosed = _.get(row, "isClosed", false);

          if (isClosed) {
            return "Close";
          }

          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {openHour.map((hour, index) => (
                <div key={`${index}-${hour}`}>
                  {hour.startTime} - {hour.endTime}
                  {index < openHour.length - 1 && <br />}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        key: "manage",
        label: "MANAGE",
        className: "text-center small",
        render: (row) => (
          <div className="times-div">
            <button
              type="button"
              className="icon-button"
              onClick={() => {
                onSelectSpecialDay(row);
                setIsOpenSpecialDay(true);
              }}
            >
              <i className="fa fa-edit"></i>
            </button>
          </div>
        ),
      },
    ],
    collection: profileSpecialDays,
  };

  return (
    <div
      className="tab-pane fade active show"
      id="reports-tab-pane"
      role="tabpanel"
      aria-labelledby="reports-tab"
      tabIndex="0"
    >
      <div className="row">
        <div className="col-lg-3">
          <h5 className="title">{t("settings.profile.business_hours")}</h5>
        </div>
        <div className="col-lg-7">
          <div className="color-gray mb-4">
            {t("settings.profile.business_hours_decs")}
          </div>
          <ul className="list-group rounded-4 overflow-hidden py-3">
            {_.map(dayKeys, (date) => {
              const businessHour = _.get(businessHours, `${date}.0`, {
                date,
              });

              const openHour = _.get(businessHour, "openHour", []);
              const isClosed = _.get(businessHour, "isClosed", false);

              return (
                <li
                  key={date}
                  className="list-group-item list-group-item-action border-0 rounded-0"
                >
                  <button
                    className="d-inline-flex day-button"
                    onClick={() => {
                      setIsOpenBusinessHour(true);
                      onSelectBusinessHours(businessHour);
                    }}
                  >
                    <span className="w-100px">{t(BusinessDates[date])}</span>
                    {isClosed ? (
                      <span className="color-info">{t("common.closed")}</span>
                    ) : openHour?.length ? (
                      <span className="color-info">
                        {openHour.map((hour, index) => (
                          <div key={`${index}-${hour}`}>
                            {hour.startTime} - {hour.endTime}
                            {index < openHour.length - 1 && <br />}
                          </div>
                        ))}
                      </span>
                    ) : (
                      <span className="color-gray">Click to setup</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <hr className="mb-4 mt-4" />
      <div className="row mb-5">
        <div className="col-lg-3">
          <h5 className="title">{t("settings.profile.special_days")}</h5>
        </div>
        <div className="col-lg-7">
          <div className="color-gray mb-4">
            {t("settings.profile.special_days_desc")}
          </div>
          <div className="row mb-3 justify-content-between">
            <div className="col-6 col-lg-3">
              {/* <Dropdown
                config={{
                  options: [
                    { name: "Tùy chọn 1", id: "option1" },
                    { name: "Tùy chọn 2", id: "option2" },
                    { name: "Tùy chọn 3", id: "option3" },
                  ],
                  onSelect: handleSelect,
                  placeholder: "Chọn tùy chọn...",
                }}
              /> */}
            </div>
            <div className="col-6 col-lg-2">
              <button
                onClick={() => {
                  onSelectSpecialDay({});
                  setIsOpenSpecialDay(true);
                }}
                className="btn btn-info-light w-100"
              >
                <i className="fa fa-plus"></i> {t("common.add")}
              </button>
            </div>
          </div>

          <div className="table-responsive table-box">
            <Table config={configTable} />
          </div>
        </div>
      </div>
      <div className="card-footer">
        <div className="d-flex gap-3 justify-content-center align-items-center">
          <button
            onClick={() => {
              navigate(`/profile/${profileId}/detail`);
            }}
            className="btn btn-gray btn-outline-gray"
          >
            {t("common.exit")}
          </button>
        </div>
      </div>
      <Popup
        config={{
          isOpen: isOpenSpecialDay,
          onClose: closePopupSpecialDay,
          title: t("settings.profile.special_days"),
        }}
      >
        <div className="modal-content">
          <div className="modal-body">
            <div className="row g-4">
              <div className="col-12">
                <label className="form-label text-uppercase">
                  {t("settings.profile.business.name")}
                </label>
                <input
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label text-uppercase">
                  {t("settings.profile.business.applied_dates")}
                </label>
                <DateRangePicker
                  config={{
                    onChange: onChangeDateRangePicker,
                    startAt,
                    endAt,
                  }}
                />
              </div>
              <div className="col-12">
                <label className="form-check form-switch gap-2 d-flex align-items-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={isClosed}
                    onChange={(e) => setIsClosed(e.target.checked)}
                  />
                  <span>
                    {" "}
                    {t("settings.profile.business.close_entire_day")}
                  </span>
                </label>
              </div>
              <div className="col-12">
                <BusinessHour
                  config={{
                    value: openHours,
                    onChange: setOpenHours,
                  }}
                />
              </div>
            </div>
          </div>
          <br />
          <div className="modal-footer">
            <div className="d-flex w-100 justify-content-between gap-2">
              <div>
                {specialDay._id && (
                  <button
                    onClick={async () => {
                      try {
                        await dispatch(
                          removeProfileBusiness(specialDay._id)
                        ).unwrap();
                        sendNotifyIziMessageSuccess(
                          t("profile.profile_special_day_remove_success")
                        );
                        dispatch(listProfileSpecialDayByProfileId(profileId));
                        closePopupSpecialDay();
                      } catch (error) {
                        sendNotifyIziMessageError(
                          t("profile.profile_special_day_remove_error")
                        );
                      }
                    }}
                    type="button"
                    className="btn btn-danger-light"
                  >
                    <i className="fa fa-trash-alt"></i> {t("common.delete")}
                  </button>
                )}
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-gray btn-outline-gray"
                  onClick={closePopupSpecialDay}
                >
                  {t("common.closed")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onSaveSpecialDay}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      <Popup
        config={{
          isOpen: isOpenBusinessHours,
          onClose: closePopupBusinessHours,
          title: t("settings.profile.business_hours"),
        }}
      >
        <div className="modal-content">
          <div className="modal-body">
            <div className="row g-4">
              <div className="col-12" data-select2-id="select2-data-45-phz4">
                <label className="form-label text-uppercase">
                  {t("settings.profile.business.date_applied")}
                </label>
                <input
                  style={{ backgroundColor: "#E1E1E7" }}
                  className="form-control"
                  value={t(BusinessDates[_.get(businessHour, "date", "")])}
                  readOnly
                />
              </div>
              <div className="col-12">
                <label className="form-check form-switch gap-2 d-flex align-items-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={isClosed}
                    onChange={(e) => setIsClosed(e.target.checked)}
                  />
                  <span>
                    {" "}
                    {t("settings.profile.business.close_entire_day")}
                  </span>
                </label>
              </div>
              <div className="col-12">
                <BusinessHour
                  config={{
                    value: openHours,
                    onChange: setOpenHours,
                  }}
                />
              </div>
              <div className="col-12">
                <label className="form-label">
                  {t("settings.profile.business.apply_other_day")}
                </label>
                <div className="d-flex gap-2 flex-nowrap">
                  {_.map(dayKeys, (date) => {
                    const businessHoursByDate = _.get(
                      businessHours,
                      date,
                      null
                    );
                    const currentBusinessHour = _.get(
                      businessHoursByDate,
                      "0",
                      null
                    );
                    const currentDate = _.get(businessHour, "date", "");

                    const isSameClosed =
                      _.get(businessHour, "isClosed", false) ===
                      _.get(currentBusinessHour, "isClosed", false);

                    const isSameOpenHour = _.isEqual(
                      _.sortBy(
                        _.map(openHours, (item) => _.omit(item, "isError")),
                        ["startTime", "endTime"]
                      ),
                      _.sortBy(_.get(currentBusinessHour, "openHour", []), [
                        "startTime",
                        "endTime",
                      ])
                    );
                    const isInOtherDay = _.includes(otherDays, date);

                    const isSame =
                      (currentBusinessHour && isSameClosed && isSameOpenHour) ||
                      isInOtherDay ||
                      currentDate === date;

                    return isSame ? (
                      <button
                        key={date}
                        type="button"
                        className="btn btn-success text-white"
                        // onClick={() => onApplyOtherDay(date, true)}
                        style={
                          currentDate === date
                            ? {
                                border: "3px solid rgb(26 104 86)",
                              }
                            : {}
                        }
                      >
                        {ShortBusinessDates[date]}
                      </button>
                    ) : (
                      <button
                        key={date}
                        className="btn btn-gray"
                        type="button"
                        // onClick={() => onApplyOtherDay(date)}
                        onClick={() => setOtherDays([...otherDays, date])}
                      >
                        {ShortBusinessDates[date]}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <br />
          <div className="modal-footer">
            <div className="d-flex w-100 justify-content-between gap-2">
              <div>
                {businessHour._id && (
                  <button
                    onClick={async () => {
                      try {
                        await dispatch(
                          removeProfileBusiness(businessHour._id)
                        ).unwrap();
                        sendNotifyIziMessageSuccess(
                          t("profile.profile_business_remove_success")
                        );
                        dispatch(listProfileBusinessHourByProfileId(profileId));
                        closePopupBusinessHours();
                      } catch (error) {
                        sendNotifyIziMessageError(
                          t("profile.profile_business_remove_error")
                        );
                      }
                    }}
                    type="button"
                    className="btn btn-danger-light"
                  >
                    <i className="fa fa-trash-alt"></i> {t("common.delete")}
                  </button>
                )}
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-gray btn-outline-gray"
                  onClick={closePopupBusinessHours}
                >
                  {t("common.closed")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onSaveBusinessHours}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default ProfileBusinessHours;
